/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  AppState,
  BinaryFiles,
} from '@excalidraw/excalidraw/dist/types/excalidraw/types';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement } from '@lexical/utils';
import { Modal } from 'antd';
import {
  $createParagraphNode,
  $insertNodes,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
} from 'lexical';
import { useEffect, useState } from 'react';

import { $createExcalidrawNode, ExcalidrawNode } from '../nodes/ExcalidrawNode';
import type { ExcalidrawInitialElements } from '../ui/ExcalidrawContainer';
import ExcalidrawContainer from '../ui/ExcalidrawContainer';

export const INSERT_EXCALIDRAW_COMMAND: LexicalCommand<void> = createCommand(
  'INSERT_EXCALIDRAW_COMMAND'
);

export const initialAppState: Partial<AppState> = {
  currentItemRoughness: 0,
  currentItemFontFamily: 2,
  objectsSnapModeEnabled: false, // Disable object snapping to prevent segment validation issues
};

export default function ExcalidrawPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!editor.hasNodes([ExcalidrawNode])) {
      throw new Error(
        'ExcalidrawPlugin: ExcalidrawNode not registered on editor'
      );
    }

    return editor.registerCommand(
      INSERT_EXCALIDRAW_COMMAND,
      () => {
        setModalOpen(true);
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  const onClose = () => {
    setModalOpen(false);
  };

  const onDelete = () => {
    setModalOpen(false);
  };

  const onSave = (
    elements: ExcalidrawInitialElements,
    appState: Partial<AppState>,
    files: BinaryFiles
  ) => {
    editor.update(() => {
      const excalidrawNode = $createExcalidrawNode();
      excalidrawNode.setData(
        JSON.stringify({
          appState,
          elements,
          files,
        })
      );
      $insertNodes([excalidrawNode]);
      if ($isRootOrShadowRoot(excalidrawNode.getParentOrThrow())) {
        $wrapNodeInElement(excalidrawNode, $createParagraphNode).selectEnd();
      }
    });
    setModalOpen(false);
  };

  return (
    <Modal
      title="Excalidraw"
      open={isModalOpen}
      onCancel={onClose}
      footer={null}
      width="95vw"
      centered
      forceRender
    >
      <ExcalidrawContainer
        initialElements={[]}
        initialAppState={initialAppState}
        initialFiles={{}}
        onDelete={onDelete}
        onClose={onClose}
        onSave={onSave}
      />
    </Modal>
  );
}
